import React, { useState } from 'react';
import {
  Box,
  Paper,
  Modal,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSummarySectionHooks } from '../../hooks/useContent';
import SummaryTabHeader from './SummaryTabHeader';
import SummaryTabContent from './SummaryTabContent';
import ChapterTabContent from './ChapterTabContent';
import SummaryErrorDialog from './SummaryErrorDialog';

const SummarySection = ({
  summary,
  detailContent,
  activeTab,
  setActiveTab,
  playerRef,
  isStreaming,
  streamingChunkSummaries,
  transcript,
  setActiveMainTab,
  videoUrl,
  videoTitle
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [toastOpen, setToastOpen] = useState(false);

  const hooks = useSummarySectionHooks({
    activeTab,
    setActiveTab,
    playerRef,
    detailContent,
    transcript,
    setActiveMainTab
  });

  const handleCopyClick = async () => {
    try {
      let clipboardContent = '';
      
      // 디버깅 로그 추가
      console.log('🔍 Copy Debug:', { 
        activeTab, 
        summary: !!summary, 
        summaryLength: summary?.length,
        detailContent: !!detailContent, 
        detailContentLength: detailContent?.length,
        videoUrl: !!videoUrl,
        videoTitle: !!videoTitle
      });
      
      if (videoUrl) {
        clipboardContent += `${t('clipboard.linkLabel')} : ${videoUrl}\n\n`;
      }
      
      if (videoTitle) {
        clipboardContent += `${t('clipboard.titleLabel')} : ${videoTitle}\n\n`;
      }
      
      let summaryContent = '';
      
      if (activeTab === 0) {
        // 핵심요약 탭
        if (summary && summary.trim()) {
          summaryContent = summary;
          console.log('✅ Summary content added:', summaryContent.substring(0, 100) + '...');
        } else {
          console.log('❌ No summary content');
        }
      } else if (activeTab === 1) {
        // 챕터별 정리 탭
        if (Array.isArray(detailContent) && detailContent.length > 0) {
          summaryContent = `[${t('clipboard.detailsLabel')}]\n`;
          detailContent.forEach((chapter, index) => {
            if (chapter.title && chapter.bullets) {
              // bullets 형태인 경우
              summaryContent += `\n${chapter.title}\n`;
              chapter.bullets.forEach(bullet => {
                summaryContent += `• ${bullet}\n`;
              });
              summaryContent += '\n';
            } else if (chapter.title && chapter.content) {
              // content 형태인 경우
              summaryContent += `\n${chapter.title}\n${chapter.content}\n`;
            } else if (chapter.content) {
              // content만 있는 경우
              summaryContent += `${chapter.content}\n\n`;
            } else {
              console.log('⚠️ Unexpected chapter format:', chapter);
            }
          });
          console.log('✅ Detail content added:', summaryContent.substring(0, 100) + '...');
        } else {
          console.log('❌ No detail content');
        }
      }
      
      if (summaryContent && summaryContent.trim()) {
        clipboardContent += summaryContent;
      } else {
        console.log('❌ No content to copy');
      }
      
      // 출처 표시 추가
      clipboardContent += `\n\n${t('clipboard.sourceAttribution')}\nhttps://tubedigest.ai`;
      
      console.log('📋 Final clipboard content length:', clipboardContent.length);
      console.log('📋 Final clipboard content preview:', clipboardContent.substring(0, 200) + '...');
      
      await navigator.clipboard.writeText(clipboardContent.trim());
      
      setToastOpen(true);
      
      setTimeout(() => {
        setToastOpen(false);
      }, 2000);
      
    } catch (error) {
      console.error('클립보드 복사 실패:', error);
    }
  };

  const handleToastClose = () => {
    setToastOpen(false);
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: isMobile ? 1 : 3,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fff',
          borderRadius: 1.5,
          boxShadow: '0 0 20px rgba(0,0,0,0.05)'
        }}
      >
        <SummaryTabHeader 
          activeTab={activeTab}
          handleTabChange={hooks.handleTabChange}
          onCopyClick={handleCopyClick}
        />

        <Box 
          ref={hooks.scrollContainerRef}
          className="scrollable-content"
          sx={{ px: 1, minHeight: 0, flex: 1, overflowY: 'auto' }}
        >
          {activeTab === 0 && (
            <SummaryTabContent
              summary={summary}
              isStreaming={isStreaming}
              streamingChunkSummaries={streamingChunkSummaries}
              expectedChunks={hooks.expectedChunks}
            />
          )}
          
          {activeTab === 1 && (
            <ChapterTabContent
              detailContent={detailContent}
              formattedDetailContent={hooks.formattedDetailContent}
              isStreaming={isStreaming}
              handleChapterClick={hooks.handleChapterClick}
              scrollToChapter={hooks.scrollToChapter}
              playerRef={playerRef}
              isMobile={isMobile}
            />
          )}
        </Box>

        <SummaryErrorDialog
          errorDialog={hooks.errorDialog}
          setErrorDialog={hooks.setErrorDialog}
        />
      </Paper>

      <Modal
        open={toastOpen}
        onClose={handleToastClose}
        hideBackdrop
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}
      >
        <Box
          sx={{
            width: '6cm',
            height: '6cm',
            backgroundColor: 'rgba(128, 128, 128, 0.9)',
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            outline: 'none',
          }}
        >
          <Box sx={{ fontSize: '48px', mb: 2 }}>
            ✅
          </Box>
          <Typography
            variant="h6"
            sx={{
              color: 'white',
              fontWeight: 600,
              textAlign: 'center',
              fontSize: '18px',
            }}
          >
            {t('clipboard.copyComplete')}
          </Typography>
        </Box>
      </Modal>
    </>
  );
};

export default SummarySection; 